import { useEffect, useState } from "react";
import { listChangeRequests } from "@/shared/api/changes";
import { listNotifications } from "@/shared/api/comments";
import { fetchDeskGlance } from "@/shared/api/desk";
import { listOverrideRequests } from "@/shared/api/overrides";
import { filterDocIds } from "@/shared/api/tags";
import {
  actOpenDocCount,
  navBadgeLabel,
  shellNotifBadgeCount,
} from "./badge-math";
import { canSeeCompanyNav } from "./nav-math";
import { hasActionPermission } from "./permission-client";

const BADGE_POLL_MS = 45_000;

/** Poll Inbox + Approvals + Act queues for shell/home badges — plan/50. */
export function useShellBadges(input: {
  companySlug: string | undefined;
  ready: boolean;
  held: readonly string[];
  poll?: boolean;
}): {
  inboxUnread: number;
  inboxApprovalUnread: number;
  approvalsPending: number;
  actOpenCount: number;
  assignedSo: number;
  assignedPo: number;
  queueCounts: {
    toCollect: number;
    toDeliver: number;
    toPay: number;
    toReceive: number;
  };
  shellBadgeCount: (opts: {
    showInbox: boolean;
    showApprovals: boolean;
    includeAct?: boolean;
  }) => number;
  itemLabel: (id: string, label: string) => string;
} {
  const { companySlug, ready, held, poll = true } = input;
  const [inboxUnread, setInboxUnread] = useState(0);
  const [inboxApprovalUnread, setInboxApprovalUnread] = useState(0);
  const [approvalsPending, setApprovalsPending] = useState(0);
  const [assignedSo, setAssignedSo] = useState(0);
  const [assignedPo, setAssignedPo] = useState(0);
  const [queueCounts, setQueueCounts] = useState({
    toCollect: 0,
    toDeliver: 0,
    toPay: 0,
    toReceive: 0,
  });

  const canApprove = hasActionPermission(held, "can_approve");
  const canSales = canSeeCompanyNav(held, "sales");
  const canPurchasing = canSeeCompanyNav(held, "purchasing");
  const heldKey = held.slice().sort().join(",");

  useEffect(() => {
    if (!companySlug || !ready) return;
    let cancelled = false;
    const heldList = heldKey ? heldKey.split(",") : [];
    const sales = canSeeCompanyNav(heldList, "sales");
    const purchasing = canSeeCompanyNav(heldList, "purchasing");
    const approve = hasActionPermission(heldList, "can_approve");

    async function refresh() {
      try {
        const notif = await listNotifications(companySlug!, true);
        if (cancelled) return;
        setInboxUnread(notif.unreadCount);
        setInboxApprovalUnread(
          notif.notifications.filter(
            (n) => !n.readAt && n.type === "approval_pending",
          ).length,
        );
      } catch {
        /* ignore poll errors */
      }

      if (!approve) {
        if (!cancelled) setApprovalsPending(0);
      } else {
        try {
          const [overrides, changes] = await Promise.all([
            listOverrideRequests(companySlug!, "pending"),
            listChangeRequests(companySlug!, "pending"),
          ]);
          if (!cancelled) {
            setApprovalsPending(
              (overrides.counts?.pending ?? overrides.requests.length) +
                (changes.counts?.pending ?? changes.requests.length),
            );
          }
        } catch {
          /* ignore */
        }
      }

      try {
        const [so, po, glance] = await Promise.all([
          sales
            ? filterDocIds(companySlug!, {
                docType: "sales_order",
                assignedToMe: true,
              })
            : Promise.resolve({ docIds: [] as string[] }),
          purchasing
            ? filterDocIds(companySlug!, {
                docType: "purchase_order",
                assignedToMe: true,
              })
            : Promise.resolve({ docIds: [] as string[] }),
          sales || purchasing
            ? fetchDeskGlance(companySlug!)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setAssignedSo(so.docIds?.length ?? 0);
        setAssignedPo(po.docIds?.length ?? 0);
        setQueueCounts({
          toCollect: sales ? (glance?.toCollect?.count ?? 0) : 0,
          toDeliver: sales ? (glance?.toDeliver?.count ?? 0) : 0,
          toPay: purchasing ? (glance?.toPay?.count ?? 0) : 0,
          toReceive: purchasing ? (glance?.toReceive?.count ?? 0) : 0,
        });
      } catch {
        if (!cancelled) {
          setAssignedSo(0);
          setAssignedPo(0);
          setQueueCounts({
            toCollect: 0,
            toDeliver: 0,
            toPay: 0,
            toReceive: 0,
          });
        }
      }
    }

    void refresh();
    if (!poll) {
      return () => {
        cancelled = true;
      };
    }
    const id = window.setInterval(() => void refresh(), BADGE_POLL_MS);
    function onFocus() {
      void refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [companySlug, ready, canApprove, heldKey, poll]);

  const actOpenCount = actOpenDocCount({
    assignedSo: canSales ? assignedSo : 0,
    assignedPo: canPurchasing ? assignedPo : 0,
    toCollect: canSales ? queueCounts.toCollect : 0,
    toDeliver: canSales ? queueCounts.toDeliver : 0,
    toPay: canPurchasing ? queueCounts.toPay : 0,
    toReceive: canPurchasing ? queueCounts.toReceive : 0,
  });

  function shellBadgeCount(opts: {
    showInbox: boolean;
    showApprovals: boolean;
    includeAct?: boolean;
  }): number {
    return shellNotifBadgeCount({
      inboxUnread,
      inboxApprovalUnread,
      approvalsPending,
      actOpenCount,
      showInbox: opts.showInbox,
      showApprovals: opts.showApprovals,
      includeAct: opts.includeAct,
    });
  }

  function itemLabel(id: string, label: string): string {
    if (id === "inbox") return navBadgeLabel(label, inboxUnread);
    if (id === "approvals") return navBadgeLabel(label, approvalsPending);
    return label;
  }

  return {
    inboxUnread,
    inboxApprovalUnread,
    approvalsPending,
    actOpenCount,
    assignedSo,
    assignedPo,
    queueCounts,
    shellBadgeCount,
    itemLabel,
  };
}
